'use client';
import { useMemo, useState } from 'react';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { SITE } from '@/app/constants';
import { cn } from '@/app/utils';
import type { AgentProjectShowCase } from '../../../_types';
import { ratioToWidthHeight } from '../../../_utils';

interface LibraryProjectCardProps {
  data: AgentProjectShowCase;
  onClick: () => void;
}

/**
 * 根据 ratio 字符串计算宽高比
 */
const getRatioValue = (ratio?: string): string => {
  switch (ratio) {
    case 'Ratio_16_9':
      return '16/9';
    case 'Ratio_9_16':
      return '9/16';
    case 'Ratio_1_1':
      return '1/1';
    case 'Ratio_4_3':
      return '4/3';
    case 'Ratio_3_4':
      return '3/4';
    case 'Ratio_21_9':
      return '21/9';
    default:
      return '16/9'; // 默认使用 16:9
  }
};

export const LibraryProjectCard = ({ data, onClick }: LibraryProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const coverUrl = data.input_image_urls?.[0] || data.cover_url || '';
  const videoUrl = data.video_url || '';
  const projectName = data.project_name || '';
  const aspectRatio = useMemo(() => getRatioValue(data.ratio), [data.ratio]);
  const [wmWidth, wmHeight] = useMemo(() => ratioToWidthHeight(data.ratio), [data.ratio]);
  const showWatermark = SITE === 'china';

  // 如果没有视频,显示图片
  if (!videoUrl || !coverUrl) {
    return (
      <div className="group relative mb-4 w-full cursor-pointer overflow-hidden rounded-8" onClick={onClick}>
        <div className="relative w-full bg-system-black" style={{ aspectRatio }}>
          <img src={coverUrl} alt={projectName} className="h-full w-full object-cover" />
          <MediaWithWatermark overlay width={wmWidth} height={wmHeight} showWatermark={showWatermark} />
        </div>
      </div>
    );
  }

  return (
    <HoverPlayMedia
      id={data.id || ''}
      uri={videoUrl}
      coverUri={coverUrl}
      onClick={onClick}
      className="group relative mb-4 w-full cursor-pointer overflow-hidden rounded-8"
      aspectRatio={aspectRatio}
      videoClassName="object-cover"
      borderRadiusClassName="rounded-8"
      eventName="library_project_card_hover"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 水印层 */}
      <MediaWithWatermark className="z-30" overlay width={wmWidth} height={wmHeight} showWatermark={showWatermark} />
      {/* 悬停遮罩 - 显示项目名称 */}
      <div
        className={cn(
          'absolute right-0 bottom-0 left-0 z-20 flex flex-col items-center justify-center p-4 transition-opacity duration-200',
          'bg-[linear-gradient(180deg,rgba(0,0,0,0.00)_0%,rgba(0,0,0,0.64)_100%)]',
          isHovered ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <div className="line-clamp-3 px-6 text-center font-semibold text-lg text-system-white">{projectName}</div>
      </div>
    </HoverPlayMedia>
  );
};
